// src/pages/coupons/CouponView.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FiArrowLeft, FiEdit3, FiCopy } from "react-icons/fi";
import "react-toastify/dist/ReactToastify.css";

const fmtMoney = (n) =>
  n == null
    ? "-"
    : Number(n).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (val) => (val ? new Date(val).toLocaleString("tr-TR") : "-");

export default function CouponView() {
  const { id } = useParams();
  const nav = useNavigate();

  const [row, setRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOne = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/coupons/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
      });
      setRow(res.data);
    } catch (e) {
      console.error("❌ Kupon detayı alınamadı:", e);
      toast.error("Kupon detayı alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const discountPretty = useMemo(() => {
    if (!row) return "-";
    return row.discount_type === "percent"
      ? `%${Number(row.discount_value || 0)}`
      : `${fmtMoney(row.discount_value)} ₺`;
  }, [row]);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(row?.code || "");
      toast.info("Kod kopyalandı");
    } catch {
      toast.warn("Kopyalanamadı");
    }
  };

  if (loading) {
    return (
      <div className="container py-4">
        <ToastContainer />
        <div className="alert alert-light">Yükleniyor…</div>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="container py-4">
        <ToastContainer />
        <div className="alert alert-danger d-flex align-items-center justify-content-between">
          <span>Kupon bulunamadı.</span>
          <Link to="/coupon/table" className="btn btn-light btn-sm">
            Geri Dön
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <ToastContainer />

      <div className="row g-4">
        {/* Sol: Detaylar */}
        <div className="col-12 col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-white d-flex align-items-center justify-content-between">
              <h6 className="m-0">Temel Bilgiler</h6>
              <span className="badge bg-light text-dark">
                {row.discount_type === "percent" ? "% Yüzde" : "₺ Tutar"}
              </span>
            </div>
            <div className="card-body">
              <div className="row gy-3">
                <div className="col-md-6">
                  <div className="text-muted small">Kod</div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-5 fw-semibold">{row.code}</span>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={copyCode}>
                      <FiCopy />
                    </button>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">İndirim</div>
                  <div className="fs-5 fw-semibold">{discountPretty}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Min. Sepet Tutarı</div>
                  <div className="fs-6">{row.min_amount == null ? "-" : `${fmtMoney(row.min_amount)} ₺`}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Kullanım Limiti</div>
                  <div className="fs-6">{row.usage_limit ?? "-"}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Başlangıç</div>
                  <div className="fs-6">{fmtDate(row.start_at)}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Bitiş</div>
                  <div className="fs-6">{fmtDate(row.end_at)}</div>
                </div>

                <div className="col-md-6">
                  <div className="text-muted small">Kullanım</div>
                  <div className="fs-6">{row.usage_count ?? 0}</div>
                </div>

                <div className="col-md-6">
                    <div className="text-muted small">Durum</div>
  <div
    className={`fs-6 ${row.is_active ? "text-success" : "text-danger"}`}
  >
    {row.is_active ? "Aktif" : "Pasif"}{" "}
    {row.is_header ? <span className="text-primary">· Header</span> : ""}
  </div>
</div>
              </div>

              <hr className="my-4" />

              <div>
                <div className="text-muted small mb-2">Geçerli Olduğu Testler</div>
                {Array.isArray(row.tests) && row.tests.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {row.tests.map((t) => (
                      <span
                        key={t.id}
                        className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle"
                      >
                        {t.name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">Bu kupon herhangi bir testte geçerli değil.</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Sağ: Özet kutusu */}
        <div className="col-12 col-lg-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h6 className="m-0">Özet</h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <span className="text-muted">Kod</span>
                <strong>{row.code}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">İndirim</span>
                <strong>{discountPretty}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Min. Tutar</span>
                <strong>{row.min_amount == null ? "-" : `${fmtMoney(row.min_amount)} ₺`}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Limit</span>
                <strong>{row.usage_limit ?? "-"}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Kullanım</span>
                <strong>{row.usage_count ?? 0}</strong>
              </div>
              <hr />
              <div className="small text-muted">
                <div className="d-flex justify-content-between">
                  <span>Başlangıç</span>
                  <strong>{fmtDate(row.start_at)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Bitiş</span>
                  <strong>{fmtDate(row.end_at)}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Durum</span>
                  <strong>{row.is_active ? "Aktif" : "Pasif"}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Header</span>
                  <strong>{row.is_header ? "Evet" : "Hayır"}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body d-flex gap-2 justify-content-between">
              <Link to={`/coupon/edit/${row.id}`} className="btn btn-primary">
                <FiEdit3 className="me-1" />
                Düzenle
              </Link>
              <Link to="/coupon/table" className="btn btn-light">
                Listeye Dön
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
